import React from "react";
import { DesignDialog, DesignDialogButton } from "./DesignDialog";

export type IdaGenerateDialogProps = {
  open: boolean;
  value: string;
  onChange: (value: string) => void;
  onUse: () => void;
  onRegenerate: () => void;
  onClose: () => void;
  loading?: boolean;
  error?: string | boolean;
  title?: string;
  description?: string;
  fieldLabel?: string;
  maxLength?: number;
};

export const IdaGenerateDialog = ({
  open,
  value,
  onChange,
  onUse,
  onRegenerate,
  onClose,
  loading = false,
  error,
  title = "Let Me Generate Your Member Description",
  description = "I generate member description based on your member name, website URL, and the contents of your description field. Review the suggested description below and edit it if needed, then click Use to apply it.",
  fieldLabel = "Suggested member description",
  maxLength = 2000,
}: IdaGenerateDialogProps) => (
  <DesignDialog
    open={open}
    onClose={onClose}
    size="ida"
    ariaLabel={title}
  >
    <div className="flex w-full flex-col gap-[12px] text-left">
      <div className="flex items-start gap-[10px]">
        <span
          className="relative block h-[52px] shrink-0 overflow-hidden"
          style={{ width: (52 * 390) / 286 }}
        >
          {/* Use the existing PNG's 390 x 286 artwork area at (55, 124). */}
          <img
            src="/ida-chatbot-icon.png"
            alt="IDA"
            className="absolute max-w-none"
            style={{
              width: (52 * 500) / 286,
              height: (52 * 500) / 286,
              left: -(52 * 55) / 286,
              top: -(52 * 124) / 286,
            }}
          />
        </span>
        <div className="mt-[2px] rounded-[12px] rounded-bl-[2px] border-[1.5px] border-[#0A0A0A] px-[12px] py-[7px] text-left text-[13px] font-semibold leading-[1.35]">
          Hi,
          <br />I am IDA
        </div>
      </div>
      <h2 className="text-[19px] font-bold leading-[normal] text-[#E1A731]">
        {title}
      </h2>
      <p className="text-[14px] leading-[1.55] text-[#0A0A0A]">
        {description}
      </p>
      <textarea
        aria-label={fieldLabel}
        rows={6}
        maxLength={maxLength}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={loading}
        className="w-full resize-y rounded-[10px] border-[1.5px] border-[#E1A731] px-[14px] py-[12px] text-left text-[14px] leading-[1.55] text-[#0A0A0A] outline-none disabled:bg-[#F7F7F8]"
      />
      {loading && (
        <div
          role="status"
          className="flex items-center gap-[8px] text-[14px] text-[#727272]"
        >
          <span
            className="h-[16px] w-[16px] animate-spin rounded-full border-2 border-[#E1A731] border-t-transparent"
            aria-hidden="true"
          />
          Generating description...
        </div>
      )}
      {error && (
        <p role="alert" className="text-[14px] text-[#E24C4C]">
          {typeof error === "string" ? error : "IDA could not generate a description. Try again."}
        </p>
      )}
      <div className="grid w-full grid-cols-2 gap-[12px]">
        <DesignDialogButton disabled={loading || !value.trim()} onClick={onUse} style={{ lineHeight: "normal" }}>
          Use
        </DesignDialogButton>
        <DesignDialogButton
          tone="green"
          disabled={loading}
          onClick={onRegenerate}
          style={{ lineHeight: "normal", gap: 9 }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M20 7v5h-5M4 17v-5h5M6 8a7 7 0 0 1 12-2l2 3M4 15l2 3a7 7 0 0 0 12-2" />
          </svg>
          Re-generate
        </DesignDialogButton>
      </div>
    </div>
  </DesignDialog>
);

export default IdaGenerateDialog;
